import { memo, useEffect, useMemo } from 'react';
import { flip, hide, offset, shift, useFloating } from '@floating-ui/react-dom';

import { usePrev } from '@libs/hooks/usePrev';
import { AnimatedNode } from '@ui/components/Animation';

import { useStreamsGameShortQuery } from '@features/shared/api/streams';
import { mapGameToGameCard } from '@features/tournament/utils/gameCardMappers';

import { GameCardGame } from '../GameCard';
import { GamePreview } from './GamePreview';

import styles from './GamePreview.module.css';

export type GamePreviewInitialData = Pick<GameCardGame, 'whiteName' | 'blackName' | 'winner'>;

export const GamePreviewWidget = memo(function GamePreviewWidget(props: {
  id?: string;
  el?: HTMLElement;
  initialData?: GamePreviewInitialData;
}) {
  const { id, el, initialData } = props;

  return (
    <AnimatedNode className={styles.wrapper} animation="half-opacity">
      {id && el && <GamePreviewFloating id={id} el={el} initialData={initialData} />}
    </AnimatedNode>
  );
});

const GamePreviewFloating = memo(function GamePreviewFloating({
  id,
  el,
  initialData,
}: {
  id: string;
  el: HTMLElement;
  initialData?: GamePreviewInitialData;
}) {
  const floating = useFloating({
    placement: 'bottom',
    transform: true,
    middleware: [offset({ mainAxis: 12 }), flip(), shift(), hide()],
  });

  useEffect(() => {
    floating.refs.setReference(el);
  }, [floating.refs, el]);

  const { data: game } = useStreamsGameShortQuery(id, {
    staleTime: Infinity,
  });

  const mapped = useMemo(() => (game ? mapGameToGameCard(game.gameId, game) : undefined), [game]);

  const initialPreview = useMemo(
    (): GameCardGame | undefined => (initialData ? { id, ...initialData } : undefined),
    [id, initialData],
  );

  const previewData = mapped ?? initialPreview;

  const style = floating.floatingStyles;
  const positioned = floating.isPositioned;
  const hidden = floating.middlewareData.hide?.referenceHidden;
  const badPositioning = !style.transform || style.transform === 'translate(0px, 0px)';
  const prevPositioned = usePrev(positioned);
  const prevHidden = usePrev(hidden);
  const noTransition = !positioned || !prevPositioned || hidden || prevHidden || badPositioning;

  return (
    <div
      className={styles.floatingContainer}
      style={{
        ...style,
        visibility: positioned && !hidden && !badPositioning ? 'visible' : 'hidden',
        transition: noTransition ? 'none' : undefined,
      }}
      ref={floating.refs.setFloating}
    >
      {previewData && <GamePreview {...previewData} />}
    </div>
  );
});
